// ========================================
// pages/AboutUs.js - Company Information
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Tell the story of Agyapadie Vault, our mission, values, and team
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// - /images/hero/hero-main.jpg - Story section image (placeholder)
// - /images/icons/feature-security.png - Security value icon
// - /images/icons/feature-tradition.png - Tradition value icon
// - /images/icons/feature-fraud.png - Fraud protection icon
// - /images/icons/feature-trustees.png - Integrity icon
// - /images/team/founder.jpg - Founder photo (placeholder)
// - /images/team/cto.jpg - CTO photo (placeholder)
// - /images/team/legal.jpg - Legal head photo (placeholder)
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';

const AboutUs = () => {
  return (
    <div className="about-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO - OUR STORY */}
      {/* ======================================== */}
      <section className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="about-container">
          <div className="about-hero-content">
            <div className="about-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="about-badge-icon" />
              <span>Our Story</span>
            </div>
            <h1 className="about-hero-title">
              Preserving <span className="gold">Legacies</span>, Protecting <span className="gold">Families</span>
            </h1>
            <p className="about-hero-subtitle">
              Agyapadie Vault was born from a simple but powerful idea: 
              no family should lose access to their loved one's assets simply 
              because important information was never shared.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: THE FOUNDING STORY */}
      {/* ======================================== */}
      <section className="about-story">
        <div className="about-container">
          <div className="about-story-grid">
            <div className="about-story-text">
              <h2 className="about-section-title">The <span className="gold">Story</span> Behind Agyapadie</h2>
              <p className="about-story-paragraph">
                The word <strong>"Agyapadie"</strong> comes from the Akan language, meaning 
                <strong> "inheritance" or "legacy"</strong>. In Ghanaian culture, passing down wealth 
                and wisdom to the next generation is a sacred duty.
              </p>
              <p className="about-story-paragraph">
                Yet, every day, families lose access to bank accounts, properties, investments, 
                and digital assets because the deceased never documented where their assets 
                were held or how to access them.
              </p>
              <p className="about-story-paragraph">
                Agyapadie Vault was founded to solve this problem. We combine traditional 
                Ghanaian values of family and inheritance with modern cryptographic technology 
                to ensure your legacy reaches the right hands.
              </p>
              <p className="about-story-paragraph">
                What started as a personal mission has grown into a platform trusted by 
                families across Ghana and beyond. We are proud to help protect what matters most.
              </p>
            </div>
            <div className="about-story-image">
              <img src="/images/hero/hero-main.jpg" alt="Ghanaian family heritage" />
              <div className="about-story-quote">
                <span className="quote-mark">"</span>
                <p>Inheritance is not just about wealth. It's about love, responsibility, and ensuring your family thrives.</p>
                <span className="quote-author">— Founder, Agyapadie Vault</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: MISSION & VISION */}
      {/* ======================================== */}
      <section className="about-mission">
        <div className="about-container">
          <div className="about-mission-grid">
            <div className="about-mission-card">
              <img src="/images/icons/feature-security.png" alt="Mission" className="about-mission-icon" />
              <h3>Our Mission</h3>
              <p>
                To provide every Ghanaian family with a secure, accessible, and trustworthy 
                platform to preserve their digital legacy and ensure their assets are 
                transferred according to their wishes.
              </p>
            </div>
            <div className="about-mission-card">
              <img src="/images/icons/feature-tradition.png" alt="Vision" className="about-mission-icon" />
              <h3>Our Vision</h3>
              <p>
                A future where no family loses access to their loved one's assets, 
                and where digital inheritance is as natural and expected as writing a will.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 4: CORE VALUES */}
      {/* ======================================== */}
      <section className="about-values">
        <div className="about-container">
          <h2 className="about-section-title">Our <span className="gold">Core Values</span></h2>
          <p className="about-section-subtitle">
            The principles that guide everything we do
          </p>
          
          <div className="about-values-grid">
            <div className="about-value-card">
              <img src="/images/icons/feature-security.png" alt="Security First" className="about-value-icon" />
              <h3>Security First</h3>
              <p>Your data is protected with bank-grade encryption and cryptographic proofs. We never compromise on security.</p>
            </div>
            <div className="about-value-card">
              <img src="/images/icons/feature-tradition.png" alt="Family First" className="about-value-icon" />
              <h3>Family First</h3>
              <p>Every feature is designed with Ghanaian family structures and inheritance customs in mind.</p>
            </div>
            <div className="about-value-card">
              <img src="/images/icons/feature-fraud.png" alt="Transparency" className="about-value-icon" />
              <h3>Transparency</h3>
              <p>We are open about how our system works, how data is protected, and how assets are transferred.</p>
            </div>
            <div className="about-value-card">
              <img src="/images/icons/feature-trustees.png" alt="Innovation" className="about-value-icon" />
              <h3>Innovation</h3>
              <p>We continuously improve our platform using the latest technology to serve you better.</p>
            </div>
            <div className="about-value-card">
              <img src="/images/icons/feature-security.png" alt="Integrity" className="about-value-icon" />
              <h3>Integrity</h3>
              <p>We do the right thing, even when no one is watching. Your trust is our most valuable asset.</p>
            </div>
            <div className="about-value-card">
              <img src="/images/icons/feature-tradition.png" alt="Accessibility" className="about-value-icon" />
              <h3>Accessibility</h3>
              <p>Our platform is designed to be easy to use for everyone, regardless of technical experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 5: THE PROBLEM WE SOLVE */}
      {/* ======================================== */}
      <section className="about-problem">
        <div className="about-container">
          <div className="about-problem-grid">
            <div className="about-problem-content">
              <h2 className="about-section-title">The <span className="gold">Problem</span> We Solve</h2>
              <p>
                Every year, billions of cedis in assets go unclaimed because families don't know 
                where their loved ones held accounts, what digital assets they owned, or how to access them.
              </p>
              <ul className="about-problem-list">
                <li>✓ Bank accounts remain dormant with no beneficiaries</li>
                <li>✓ Properties are lost because no one knows about them</li>
                <li>✓ Digital assets (crypto, online accounts) become inaccessible</li>
                <li>✓ Family disputes arise due to unclear inheritance wishes</li>
                <li>✓ Vital documents are never found</li>
              </ul>
              <p>
                Agyapadie Vault solves this by providing a single, secure place to document 
                everything and a trusted process to release it to the right people.
              </p>
            </div>
            <div className="about-problem-stats">
              <div className="about-stat-card">
                <div className="about-stat-number">GHS 2B+</div>
                <div className="about-stat-label">Unclaimed Assets (Ghana)</div>
              </div>
              <div className="about-stat-card">
                <div className="about-stat-number">70%</div>
                <div className="about-stat-label">of families don't know where assets are held</div>
              </div>
              <div className="about-stat-card">
                <div className="about-stat-number">5+ years</div>
                <div className="about-stat-label">assets can remain unclaimed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 6: OUR SOLUTION */}
      {/* ======================================== */}
      <section className="about-solution">
        <div className="about-container">
          <div className="about-solution-content">
            <h2 className="about-section-title white">Our <span className="gold">Solution</span></h2>
            <p className="about-solution-subtitle">
              A complete digital inheritance platform designed for Ghanaian families
            </p>
            
            <div className="about-solution-grid">
              <div className="about-solution-item">
                <img src="/images/icons/feature-security.png" alt="Secure Storage" className="about-solution-icon" />
                <div>
                  <h3>Secure Asset Storage</h3>
                  <p>Store all your assets in one encrypted vault</p>
                </div>
              </div>
              <div className="about-solution-item">
                <img src="/images/icons/feature-tradition.png" alt="Beneficiary Management" className="about-solution-icon" />
                <div>
                  <h3>Beneficiary Management</h3>
                  <p>Designate Global NOKs and external beneficiaries</p>
                </div>
              </div>
              <div className="about-solution-item">
                <img src="/images/icons/feature-trustees.png" alt="Trustee Verification" className="about-solution-icon" />
                <div>
                  <h3>Trustee Verification</h3>
                  <p>Trusted individuals verify death before asset release</p>
                </div>
              </div>
              <div className="about-solution-item">
                <img src="/images/icons/feature-fraud.png" alt="Cryptographic Proofs" className="about-solution-icon" />
                <div>
                  <h3>Cryptographic Proofs</h3>
                  <p>Blockchain-based integrity verification for every asset</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 7: TEAM / LEADERSHIP */}
      {/* ======================================== */}
      <section className="about-team">
        <div className="about-container">
          <h2 className="about-section-title">Meet Our <span className="gold">Leadership</span></h2>
          <p className="about-section-subtitle">
            The people behind Agyapadie Vault
          </p>
          
          <div className="about-team-grid">
            <div className="about-team-card">
              <div className="about-team-image">
                <img src="/images/team/founder.jpg" alt="Founder" />
              </div>
              <h3>Kwame Asante</h3>
              <p className="about-team-title">Founder & CEO</p>
              <p className="about-team-bio">
                Former banking executive with 15+ years experience in wealth management. 
                Founded Agyapadie Vault after seeing families struggle with unclaimed assets.
              </p>
            </div>
            <div className="about-team-card">
              <div className="about-team-image">
                <img src="/images/team/cto.jpg" alt="CTO" />
              </div>
              <h3>Abena Osei</h3>
              <p className="about-team-title">Chief Technology Officer</p>
              <p className="about-team-bio">
                Cybersecurity expert with a passion for building secure, user-friendly platforms. 
                Leads our engineering and security teams.
              </p>
            </div>
            <div className="about-team-card">
              <div className="about-team-image">
                <img src="/images/team/legal.jpg" alt="Legal" />
              </div>
              <h3>Nana Yaw Mensah</h3>
              <p className="about-team-title">Head of Legal & Compliance</p>
              <p className="about-team-bio">
                Estate planning attorney ensuring Agyapadie Vault meets all regulatory 
                requirements and protects user interests.
              </p>
            </div>
          </div>
          
          <p className="about-team-note">
            We are growing! <Link to="/careers" className="about-team-link">View open positions →</Link>
          </p>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 8: STATISTICS / TRUST INDICATORS */}
      {/* ======================================== */}
      <section className="about-stats">
        <div className="about-container">
          <div className="about-stats-grid">
            <div className="about-stat">
              <div className="about-stat-value">10,000+</div>
              <div className="about-stat-label">Active Users</div>
            </div>
            <div className="about-stat">
              <div className="about-stat-value">GHS 500M+</div>
              <div className="about-stat-label">Assets Protected</div>
            </div>
            <div className="about-stat">
              <div className="about-stat-value">50+</div>
              <div className="about-stat-label">Trustee Invitations Sent</div>
            </div>
            <div className="about-stat">
              <div className="about-stat-value">24/7</div>
              <div className="about-stat-label">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 9: CALL TO ACTION */}
      {/* ======================================== */}
      <section className="about-cta">
        <div className="about-container">
          <div className="about-cta-content">
            <h2 className="about-cta-title">Join the Agyapadie Community</h2>
            <p className="about-cta-subtitle">
              Start protecting your legacy today. It takes less than 5 minutes to create your free account.
            </p>
            <div className="about-cta-buttons">
              <Link to="/register" className="about-cta-primary">Create Free Account</Link>
              <Link to="/contact" className="about-cta-secondary">Contact Our Team</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .about-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .about-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold {
          color: #FFD700;
        }
        
        .white {
          color: white;
        }
        
        /* Hero Section */
        .about-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .about-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .about-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .about-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .about-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .about-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .about-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .about-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Section Titles */
        .about-section-title {
          font-size: 40px;
          text-align: center;
          margin-bottom: 20px;
          color: #0A0A0A;
        }
        
        .about-section-subtitle {
          font-size: 18px;
          text-align: center;
          color: #666;
          margin-bottom: 60px;
        }
        
        /* Story Section */
        .about-story {
          padding: 80px 0;
          background: white;
        }
        
        .about-story-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        
        .about-story-paragraph {
          margin-bottom: 20px;
          line-height: 1.7;
          color: #444;
        }
        
        .about-story-image img {
          width: 100%;
          border-radius: 15px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        
        .about-story-quote {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          margin-top: 30px;
          position: relative;
        }
        
        .quote-mark {
          font-size: 48px;
          color: #FFD700;
          font-family: serif;
          position: absolute;
          top: 10px;
          left: 20px;
          opacity: 0.3;
        }
        
        .about-story-quote p {
          font-style: italic;
          font-size: 16px;
          line-height: 1.6;
          margin-bottom: 15px;
          padding-left: 20px;
        }
        
        .quote-author {
          font-size: 14px;
          color: #666;
          font-style: normal;
          padding-left: 20px;
        }
        
        /* Mission Section */
        .about-mission {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .about-mission-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 40px;
        }
        
        .about-mission-card {
          background: white;
          padding: 40px;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
        }
        
        .about-mission-icon {
          height: 70px;
          width: auto;
          margin-bottom: 20px;
        }
        
        .about-mission-card h3 {
          font-size: 24px;
          margin-bottom: 15px;
          color: #0A0A0A;
        }
        
        .about-mission-card p {
          color: #555;
          line-height: 1.6;
        }
        
        /* Values Section */
        .about-values {
          padding: 80px 0;
          background: white;
        }
        
        .about-values-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        .about-value-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          transition: transform 0.3s;
        }
        
        .about-value-card:hover {
          transform: translateY(-5px);
        }
        
        .about-value-icon {
          height: 60px;
          width: auto;
          margin-bottom: 20px;
        }
        
        .about-value-card h3 {
          font-size: 20px;
          margin-bottom: 15px;
        }
        
        .about-value-card p {
          color: #555;
          line-height: 1.6;
        }
        
        /* Problem Section */
        .about-problem {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .about-problem-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        
        .about-problem-list {
          list-style: none;
          margin: 25px 0;
        }
        
        .about-problem-list li {
          margin-bottom: 12px;
          color: #444;
        }
        
        .about-problem-stats {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .about-stat-card {
          background: white;
          padding: 25px;
          border-radius: 10px;
          text-align: center;
          border-left: 4px solid #FFD700;
        }
        
        .about-stat-number {
          font-size: 32px;
          font-weight: bold;
          color: #FFD700;
          margin-bottom: 5px;
        }
        
        .about-stat-label {
          font-size: 14px;
          color: #666;
        }
        
        /* Solution Section */
        .about-solution {
          padding: 80px 0;
          background: #0A0A0A;
        }
        
        .about-solution-content {
          text-align: center;
        }
        
        .about-solution-subtitle {
          color: #ccc;
          margin-bottom: 50px;
        }
        
        .about-solution-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 30px;
          max-width: 800px;
          margin: 0 auto;
          text-align: left;
        }
        
        .about-solution-item {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          background: rgba(255,255,255,0.05);
          padding: 20px;
          border-radius: 10px;
        }
        
        .about-solution-icon {
          height: 40px;
          width: auto;
        }
        
        .about-solution-item h3 {
          font-size: 18px;
          margin-bottom: 8px;
          color: #FFD700;
        }
        
        .about-solution-item p {
          font-size: 14px;
          color: #ccc;
        }
        
        /* Team Section */
        .about-team {
          padding: 80px 0;
          background: white;
        }
        
        .about-team-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 40px;
          margin-bottom: 30px;
        }
        
        .about-team-card {
          text-align: center;
        }
        
        .about-team-image {
          width: 200px;
          height: 200px;
          margin: 0 auto 20px;
          border-radius: 50%;
          overflow: hidden;
          background: #F5F0E6;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .about-team-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .about-team-card h3 {
          font-size: 22px;
          margin-bottom: 5px;
        }
        
        .about-team-title {
          color: #FFD700;
          font-weight: bold;
          margin-bottom: 10px;
        }
        
        .about-team-bio {
          font-size: 14px;
          color: #666;
          line-height: 1.5;
        }
        
        .about-team-note {
          text-align: center;
          margin-top: 40px;
        }
        
        .about-team-link {
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
        }
        
        /* Statistics Section */
        .about-stats {
          padding: 60px 0;
          background: #FFD700;
        }
        
        .about-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 40px;
          text-align: center;
        }
        
        .about-stat-value {
          font-size: 36px;
          font-weight: bold;
          color: #0A0A0A;
          margin-bottom: 10px;
        }
        
        .about-stat-label {
          font-size: 14px;
          color: #333;
        }
        
        /* CTA Section */
        .about-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          text-align: center;
        }
        
        .about-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .about-cta-title {
          font-size: 36px;
          color: white;
          margin-bottom: 20px;
        }
        
        .about-cta-subtitle {
          font-size: 18px;
          color: #ccc;
          margin-bottom: 30px;
        }
        
        .about-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .about-cta-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .about-cta-primary:hover {
          background: #B8860B;
        }
        
        .about-cta-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .about-cta-secondary:hover {
          background: #FFD700;
          color: #0A0A0A;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
          .about-hero-title {
            font-size: 40px;
          }
          
          .about-section-title {
            font-size: 32px;
          }
          
          .about-values-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .about-team-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .about-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .about-hero-title {
            font-size: 28px;
          }
          
          .about-hero-subtitle {
            font-size: 16px;
          }
          
          .about-section-title {
            font-size: 24px;
          }
          
          .about-story-grid {
            grid-template-columns: 1fr;
          }
          
          .about-mission-grid {
            grid-template-columns: 1fr;
          }
          
          .about-values-grid {
            grid-template-columns: 1fr;
          }
          
          .about-problem-grid {
            grid-template-columns: 1fr;
          }
          
          .about-solution-grid {
            grid-template-columns: 1fr;
          }
          
          .about-team-grid {
            grid-template-columns: 1fr;
          }
          
          .about-stats-grid {
            grid-template-columns: 1fr;
          }
          
          .about-cta-title {
            font-size: 24px;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default AboutUs;